/*
   This component, EventsCalendarDaySelector, provides a weekly calendar interface for selecting dates.
   It includes a desktop and mobile view with a mobile week selector.

   The component handles date selection, week navigation, and applies various date-based conditions to
   manage state and UI updates.
*/

'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  format,
  addDays,
  isSameDay,
  startOfWeek,
  isBefore,
  isAfter,
  isSameWeek,
  Day,
} from 'date-fns';
import { de } from 'date-fns/locale'; // Import German locale
import Image from 'next/image';
import { MobileWeekSelector } from './EventsCalendarMobileWeekSelector';
import {
  MATOMO_ACTIONS,
  MATOMO_CATEGORIES,
  MATOMO_NAMES,
  trackMatomoEvent,
} from '@/utils/matomo';
import styles from './EventsCalendarDaySelector.module.scss';

type DayGridProps = {
  onDateClick: (date: Date) => void;
  // Server time as the base instead of the client clock, so the date boundaries
  // cannot be manipulated (e.g. by setting the clock into the future).
  serverNow: Date;
  className?: string;
};

export const EventsCalendarDaySelector: React.FC<DayGridProps> = ({
  onDateClick,
  serverNow,
  className,
}) => {
  const tomorrow = addDays(serverNow, 1);
  const yesterday = addDays(serverNow, -1);
  const fourWeeksFromNow = addDays(serverNow, 28);
  const [selectedWeek, setSelectedWeek] = useState<Date>(tomorrow); // Responsibile for the current week in the day selecter
  const [selectedDate, setSelectedDate] = useState<Date>(); // The highlighted date
  const [windowWidth, setWindowWidth] = useState(700); // State to manage window width init value will be overwritten by useEffect

  const [weekIndex, setWeekIndex] = useState(0); // 0 = Current Week, 1 = Next Week, etc.

  const dayRowRef = useRef<HTMLDivElement>(null);
  const activeCellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        return setWindowWidth(window.innerWidth);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isEndOfNextWeekBeyondFourWeeks = isAfter(
    addDays(startOfWeek(addDays(selectedWeek, 7), { weekStartsOn: 1 }), 6),
    fourWeeksFromNow,
  );
  const isCurrentWeek = isSameDay(
    startOfWeek(serverNow, { weekStartsOn: 1 }),
    startOfWeek(selectedWeek, { weekStartsOn: 1 }),
  );

  // Generate an array of 7 days starting from the beginning of the selected week
  const days = Array.from({ length: 7 }).map((_, index) => {
    const date = addDays(startOfWeek(selectedWeek, { weekStartsOn: 1 }), index);
    return {
      dayOfWeek: format(date, 'EE', { locale: de }).replace(/\./g, ''), // Remove all periods, // Day of the week in two letters, German
      formatted: format(date, 'dd.MM.'), // Date in dd.MM format
      dateObject: date,
      isToday: isSameDay(date, serverNow),
      isTomorrow: isSameDay(date, tomorrow),
      isSelected: selectedDate ? isSameDay(date, selectedDate) : false,
      isPast: isBefore(date, yesterday),
      isBeyond28Days: isAfter(date, fourWeeksFromNow), // Check if the date is beyond 30 days from now
    };
  });

  const nextWeek = () => {
    if (!isEndOfNextWeekBeyondFourWeeks) {
      const newWeek = addDays(selectedWeek, 7);
      setSelectedWeek(newWeek);

      const monday = startOfWeek(newWeek, { weekStartsOn: 1 });
      handleDateClick(monday);

      const newIndex = weekIndex + 1;
      setWeekIndex(newIndex);

      trackMatomoEvent(
        MATOMO_CATEGORIES.INTERACTIONS,
        MATOMO_ACTIONS.WEEK_CHANGE,
        MATOMO_NAMES.CALENDAR_WEEK_CHANGE,
        newIndex,
      );
    }
  };

  const prevWeek = () => {
    if (!isCurrentWeek) {
      const newWeek = addDays(selectedWeek, -7);
      setSelectedWeek(newWeek);

      // When going back to the current week, select the first non-past day (today)
      const monday = startOfWeek(newWeek, { weekStartsOn: 1 });
      const firstValidDay = isBefore(monday, addDays(serverNow, -1)) ? serverNow : monday;
      handleDateClick(firstValidDay);

      const newIndex = weekIndex - 1;
      setWeekIndex(newIndex);

      trackMatomoEvent(
        MATOMO_CATEGORIES.INTERACTIONS,
        MATOMO_ACTIONS.WEEK_CHANGE,
        MATOMO_NAMES.CALENDAR_WEEK_CHANGE,
        newIndex,
      );
    }
  };

  const handleDateClick = async (date: Date) => {
    if (!isBefore(date, yesterday) && !isAfter(date, fourWeeksFromNow)) {
      setSelectedDate(date);
      onDateClick(date);

      trackMatomoEvent(
        MATOMO_CATEGORIES.INTERACTIONS,
        MATOMO_ACTIONS.DAY_CHANGE,
        MATOMO_NAMES.CALENDAR_DAY_CHANGE,
      );
    }
  };

  // Load helper function to determine if the div should be centered on mobile when less than 7 days are displayed
  const centerDiv = shouldCenterDiv(days, windowWidth);

  // Scroll the active cell (selected day or tomorrow as default) into the horizontal
  // center of the scrollable row. The browser clamps scrollLeft, so at the start/end
  // of the bar the cell simply stays in view.
  useEffect(() => {
    const container = dayRowRef.current;
    if (!container) return;
    const cell = activeCellRef.current;
    if (!cell) {
      container.scrollLeft = 0;
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const cellLeftWithinContainer =
      cellRect.left - containerRect.left + container.scrollLeft;
    container.scrollLeft =
      cellLeftWithinContainer - container.clientWidth / 2 + cell.offsetWidth / 2;
  }, [selectedWeek, selectedDate, windowWidth]);

  const getDayCellClass = (day: (typeof days)[number], index: number) => {
    const isLastDay = index === days.length - 1;
    const classes = [styles.dayCell];

    if (index === 0) classes.push(styles.first);
    if (isLastDay) classes.push(styles.last);

    if (day.isSelected) {
      classes.push(styles.selected);
      if (day.isToday) classes.push(styles.selectedToday);
    } else if (day.isTomorrow && !selectedDate) {
      classes.push(styles.tomorrowDefault);
    } else if (day.isTomorrow) {
      classes.push(styles.tomorrowWithSelection);
    } else if (day.isToday) {
      classes.push(styles.today);
    } else if (day.isPast || day.isBeyond28Days) {
      classes.push(styles.past);
    }

    return classes.join(' ');
  };

  return (
    <div className={className}>
      <div className={styles.mobileWeekSelector}>
        <MobileWeekSelector
          isCurrentWeek={isCurrentWeek}
          currentDate={selectedWeek}
          serverNow={serverNow}
          prevWeek={prevWeek}
          nextWeek={nextWeek}
        />
      </div>
      <div ref={dayRowRef} className={styles.dayRow}>
        <div className={styles.dayBar}>
          <button
            className={[
              styles.navButtonPrev,
              isCurrentWeek ? styles.navButtonDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={prevWeek}
            disabled={isCurrentWeek}
          >
            <Image src="/icons/chevron_left.svg" alt="Previous" width={50} height={50} />
          </button>

          <div className={styles.daysContainer}>
            {days.map((day, index) => {
              return (
                <div
                  key={index}
                  ref={
                    day.isSelected || (!selectedDate && day.isTomorrow)
                      ? activeCellRef
                      : null
                  }
                  className={getDayCellClass(day, index)}
                  onClick={() =>
                    !day.isPast && !day.isBeyond28Days && handleDateClick(day.dateObject)
                  }
                >
                  <div className={styles.dayOfWeek}>{day.dayOfWeek}</div>
                  <div className={styles.dayDate}>{day.formatted}</div>
                </div>
              );
            })}
          </div>
          <button
            className={[
              styles.navButtonNext,
              isEndOfNextWeekBeyondFourWeeks ? styles.navButtonDisabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={nextWeek}
          >
            <Image src="/icons/chevron_right.svg" alt="Next" width={50} height={50} />
          </button>
        </div>
      </div>
    </div>
  );
};

const breakpoints: { [key: number]: number } = {
  7: 780,
  6: 640,
  5: 600,
  4: 500,
  3: 350,
  2: 300,
};

// Helper function to determine if the div should be centered
export const shouldCenterDiv = (days: any[], windowWidth: number): boolean => {
  const futureDaysCount = days.filter((day) => !day.isPast).length;
  const breakpoint = breakpoints[Math.min(futureDaysCount, 7)]; // Use futureDaysCount or 7 if it's greater
  return windowWidth > breakpoint;
};
